import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC, Fragment, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { SkeletonTheme } from 'react-loading-skeleton';
import ModalCarousel from '~/components/ImagesReviewCarousel/ModalCarousel';
import Star from '~/components/Star';
import { NextBtn, PrevBtn } from '~/components/TabReviews/modal-write-review/step-review';
import Colors from '~/constants/colors';
import { PATH } from '~/constants/enum';
import { ReviewItem } from '~/data/product/interfaces';
import { getUrlCustom } from '~/utils/theme';
import ReadMoreText from './readmore-text';
import styles from './StoreReviewItem.module.scss';
import { isEmpty } from 'lodash';
import useClient from '~/hooks/useClient';

dayjs.extend(relativeTime);

const formatRelativeTime = (date: string) => {
    try {
        return dayjs(date).fromNow();
    } catch (error) {
        console.log('Error in formatting date', error);
        return date;
    }
};
export type ItemStoreReview = {
    id: string;
    text: string;
    images: any[];
    product: {
        name: string;
        url_key: string;
        thumbnail: {
            url: string;
            label: string;
        };
    };
};

interface StoreReviewItemProps {
    item?: ReviewItem;
    loading?: boolean;
}

const getReviewItemByImgUrl = (items: ReviewItem[], url: string) => {
    if (!items) return null;
    return items.find((item) => item?.images?.includes(url));
};
const renderArrow = (type?: 'prev' | 'next') => {
    if (type === 'prev') {
        return <PrevBtn />;
    } else {
        return <NextBtn />;
    }
};

const StoreReviewItem: FC<StoreReviewItemProps> = ({ item, loading }) => {
    const isClient = useClient();

    const { average_rating = 0, text = '', created_at, nickname = '', product, images = [], summary } = item || {};

    const [initialSlide, setInitialSlide] = useState(0);
    const [openLightBox, setOpenLightBox] = useState(false);
    const [currentLightBoxIndex, setCurrentLightBoxIndex] = useState(0);
    const [open, setOpen] = useState(false);

    const productSmallImage = product?.small_image?.url || '';
    const productName = product?.name || '';
    const urlKey = product?.url_key || '';
    const replies = item?.replies || [];
    const isLoading = loading;
    const instance = useMemo(() => {
        if (!!created_at && !!isClient && dayjs(created_at).isValid()) {
            return formatRelativeTime(created_at);
        }
    }, [created_at, isClient]);
    const onClose = () => {
        setOpen(false);
    };

    if (!!isLoading) {
        return (
            <div className={classNames(styles.store_review_item)}>
                <SkeletonTheme baseColor="#E0E0E0" highlightColor="#F5F5F5">
                    <div className="flex flex-col gap-2">
                        <div
                            className="flex items-center text-xs flex-wrap text-text-light gap-1 mt-1"
                            style={{ color: Colors.GREY_10 }}
                        >
                            <Skeleton width={100} height={16} />
                            <span>|</span>
                            <div className="flex items-center rounded-md max-w-max gap-1">
                                <Skeleton circle={true} height={16} width={16} />
                                <Skeleton width={50} height={16} />
                            </div>
                            <span>|</span>
                            <Skeleton width={50} height={16} />
                        </div>
                        <div className="flex gap-2 items-center">
                            <Skeleton width={100} height={16} />
                        </div>
                        <div>
                            <Skeleton count={2} />
                        </div>
                        <div className="flex gap-2">
                            <Skeleton width={50} height={50} />
                            <Skeleton width={50} height={50} />
                            <Skeleton width={50} height={50} />
                        </div>
                        <div className="text-xs mt-2">
                            <Skeleton width={200} height={16} />
                        </div>
                    </div>
                </SkeletonTheme>
            </div>
        );
    }

    const isUnVerify = product?.sku === 'REVIEW';
    if (!isClient) return null;

    return (
        <Fragment>
            <div className={classNames('flex flex-col gap-2')}>
                <div
                    className={`flex items-center text-xs flex-wrap text-text-light gap-1 mt-1`}
                    style={{ color: Colors.GREY_10 }}
                >
                    <span className={`font-semibold`}>{nickname}</span>
                    {!isUnVerify && <span>|</span>}
                    {!isUnVerify && (
                        <div className={'flex items-center rounded-md max-w-max gap-1'}>
                            <img src="/svgs/checked.svg" alt="Checked" width={16} height={16} />
                            <span className={`text-xs`}>Verified</span>
                        </div>
                    )}
                    <span>|</span>
                    <span className={`text-xs`} style={{ color: Colors.GREY_10 }}>
                        {instance}
                    </span>
                </div>
                <div className="flex gap-2 items-center">
                    <span>
                        <Star size={16} value={(average_rating * 5) / 100} />
                    </span>
                </div>

                <div>
                    {/* <div>
            <div className="font-semibold mt-1">{summary}</div>
          </div> */}
                    {!!text && <ReadMoreText maxLines={2} text={text} />}
                </div>
                {!isEmpty(images) && (
                    <div className="flex gap-2">
                        {images.map((image, index) => (
                            <img
                                key={index}
                                src={image}
                                onClick={() => {
                                    setOpen(true);
                                    setInitialSlide(index);
                                }}
                                alt="Review"
                                className="w-16 h-16 rounded cursor-pointer object-cover mr-2 no-js-0"
                            />
                        ))}
                    </div>
                )}
                {!!item?.images?.length && (
                    <ModalCarousel
                        initialSlide={initialSlide}
                        images={item?.images}
                        items={[item]}
                        onClose={() => setOpen(false)}
                        open={open}
                    />
                )}

                {!!product && product?.sku !== 'REVIEW' && (
                    <div>
                        <div className="md:line-clamp-1 mb-1">
                            <span className="text-xs font-semibold">Purchased product:</span>
                            <a
                                className="cursor-pointer 
              transform duration-300 hover:underline text-xs text-text-light"
                                href={getUrlCustom({
                                    pathname: PATH.ProductDetail,
                                    query: {
                                        slug: urlKey,
                                        variant: product?.variants?.[0]?.product?.id
                                    }
                                })}
                            >
                                <span className="ms-1 inline">{productName}</span>
                            </a>
                        </div>
                    </div>
                )}

                {replies?.map((reply, index) => (
                    <div key={index} className="flex flex-col gap-2 bg-gray-100 pt-4 px-4 rounded-md">
                        <div className="flex items-center text-xs flex-wrap text-text-light gap-1 mt-1">
                            <span className={`font-semibold`}>{`Reply from ${reply.reply_by}`}</span>
                            <span>|</span>
                            <span className={`text-xs`} style={{ color: Colors.GREY_10, marginBottom: 0 }}>
                                {formatRelativeTime(reply.created_at)}
                            </span>
                        </div>
                        <div>
                            <ReadMoreText maxLines={2} text={reply.content} />
                        </div>
                    </div>)
                )}

            </div>
        </Fragment>
    );
};

export default StoreReviewItem;
